import { useState } from 'react'
import {
  Box,
  Stack,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { TpTabs } from '~/common/components/tp-tabs'
import { TaskVelocityChart } from '~/metrics/org-metrics/task-velocity-chart'
import { NumericTotals } from '~/metrics/org-metrics/numeric-totals'
import { GddChart } from '~/metrics/climate/gdd-chart'
import { CropHarvestTable } from '~/metrics/org-metrics/crop-harvest-table'

const IS_ROW_BREAKPOINT = 'lg'

/**
 * Season snapshot metrics section.
 * Left (2fr): tabbed — Performance (GDD + task velocity) | Crops (harvest table)
 * Right (1fr): NumericTotals — always visible
 *
 * Both tab panels stay mounted. Switching tabs only moves a CSS transform —
 * no React remount, no chart re-initialization, no repaint.
 *
 * @param {Object[]} crops - From useCropData()
 * @param {Array|null} gdd5Series
 * @param {Array|null} gdd10Series
 * @param {number|null} lfd
 * @param {number|null} ffd
 */
export const SeasonMetrics = ({ crops, gdd5Series = null, gdd10Series = null, lfd = null, ffd = null }) => {
  const theme = useTheme()
  const isRow = useMediaQuery(theme.breakpoints.up(IS_ROW_BREAKPOINT))
  const [tab, setTab] = useState(0)

  return (
    <Stack
      sx={{
        display: 'grid',
        gridTemplateColumns: isRow ? '4fr 1fr' : '1fr',
        gap: '1.5rem',
        rowGap: '0.25rem',
        alignItems: 'start',
      }}
    >
      {/* Left — tabs */}
      <Stack>
        <TpTabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Performance" />
          <Tab label="Crops" />
        </TpTabs>

        {/*
          CSS slider — both panels stay mounted side-by-side in a 200%-wide track.
          Only a GPU-composited translateX transitions on tab change.
          Panel width is 50% of track = 100% of the visible container.
        */}
        <Box sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              width: '200%',
              transform: tab === 0 ? 'translateX(0%)' : 'translateX(-50%)',
              transition: 'transform 200ms ease-out',
              willChange: 'transform',
              alignItems: 'flex-start',
            }}
          >
            {/* Panel 0 — Performance */}
            <Box sx={{ width: '50%', flexShrink: 0, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                GDD accumulation recorded this season will be factored into climate baselines for future seasons.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: '1rem',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ height: 200 }}>
                  <GddChart gdd5Series={gdd5Series} gdd10Series={gdd10Series} lfd={lfd} ffd={ffd} />
                </Box>
                <Box sx={{ height: 200 }}>
                  <TaskVelocityChart />
                </Box>
              </Box>
            </Box>

            {/* Panel 1 — Crops */}
            <Box sx={{ width: '50%', flexShrink: 0, minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Crop performance this season — successions, harvests, and total yield per crop.
              </Typography>
              <CropHarvestTable crops={crops} minHeight={0} maxHeight={200} />
            </Box>
          </Box>
        </Box>
      </Stack>

      {/* Right — totals always visible */}
      <NumericTotals flatList />
    </Stack>
  )
}
